import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBELLES_STATUT_ANNONCE,
  STATUTS_ANNONCE,
  auMoins,
  type AnnonceResume,
  type ResumeConservation,
  type StatutAnnonce,
} from '@rh/shared';
import { appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { NouvelleAnnonceRs } from '../composants/NouvelleAnnonceRs';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { jourLisible } from '../format';

function libelleStatut(statut: string): string {
  return (STATUTS_ANNONCE as readonly string[]).includes(statut)
    ? LIBELLES_STATUT_ANNONCE[statut as StatutAnnonce]
    : statut;
}

/**
 * Point d'entrée : brouillons en tête, puis annonces ouvertes et leurs
 * compteurs. Chaque annonce porte une case « cacher » (demande du 2026-09-19) :
 * elle la range hors de la liste de CE compte — rien n'est archivé, les
 * candidatures continuent d'arriver, et « Montrer les annonces cachées » les
 * ramène.
 */
export function Annonces() {
  const { utilisateur } = useAuth();
  const [annonces, setAnnonces] = useState<AnnonceResume[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [conservation, setConservation] = useState<ResumeConservation | null>(null);
  const [montrerCachees, setMontrerCachees] = useState(false);
  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;

  /** Coche : l'annonce est rangée (ou ressortie) tout de suite à l'écran, puis en base. */
  async function cacher(annonce: AnnonceResume, cachee: boolean): Promise<void> {
    const avant = annonces;
    setAnnonces((liste) => liste?.map((a) => (a.id === annonce.id ? { ...a, cachee } : a)) ?? liste);
    try {
      await appel(`/annonces/${annonce.id}/cachee`, { method: 'PUT', body: JSON.stringify({ cachee }) });
    } catch (e) {
      setAnnonces(avant);
      setErreur(e instanceof Error ? e.message : 'Annonce non rangée');
    }
  }

  /** Relue après la création d'une annonce RS, qui n'est pas passée par ici. */
  const recharger = useCallback(() => {
    appel<AnnonceResume[]>('/annonces')
      .then(setAnnonces)
      .catch((e: Error) => setErreur(e.message));
  }, []);

  useEffect(recharger, [recharger]);

  useEffect(() => {
    if (!rh) return;
    // Simple bandeau d'information : son échec ne bloque pas la page.
    appel<ResumeConservation>('/conservation/resume')
      .then(setConservation)
      .catch(() => setConservation(null));
  }, [rh]);

  if (erreur && !annonces) return <Erreur message={erreur} />;
  if (!annonces) return <Chargement quoi="des annonces" />;

  const visibles = annonces.filter((annonce) => !annonce.cachee);
  const cachees = annonces.filter((annonce) => annonce.cachee);

  return (
    <section>
      <div className="entete-annonce">
        <h2>Annonces</h2>
        {rh && (
          <div className="actions-entete">
            <Link className="bouton lien-bouton" to="/annonces/nouvelle">
              Nouvelle annonce
            </Link>
            {/* Poste publié seulement sur les réseaux : titre, métier, lien. */}
            <NouvelleAnnonceRs surCreation={recharger} />
          </div>
        )}
      </div>

      {erreur && <Erreur message={erreur} />}

      {conservation && conservation.enPreavis > 0 && conservation.prochainePurge && (
        <p className="signal signal-alerte bandeau-conservation">
          {conservation.enPreavis === 1
            ? '1 fiche candidat sera anonymisée'
            : `${conservation.enPreavis} fiches candidats seront anonymisées`}{' '}
          à partir du {jourLisible(conservation.prochainePurge)} — <Link to="/conservation">voir et prolonger</Link>
        </p>
      )}

      {visibles.length === 0 && cachees.length === 0 && <Vide enfants="Aucune annonce ouverte." />}
      {visibles.length === 0 && cachees.length > 0 && !montrerCachees && (
        <Vide enfants="Toutes vos annonces sont cachées." />
      )}

      {cachees.length > 0 && (
        <div className="outils-liste">
          <button type="button" className="bouton-second" onClick={() => setMontrerCachees((v) => !v)}>
            {montrerCachees
              ? 'Masquer les annonces cachées'
              : `Montrer les annonces cachées (${cachees.length})`}
          </button>
        </div>
      )}

      <ul className="annonces">
        {(montrerCachees ? [...visibles, ...cachees] : visibles).map((annonce) => {
          const brouillon = annonce.statut === 'brouillon';
          return (
            <li key={annonce.id} className={annonce.cachee ? 'annonce-cachee' : undefined}>
              <input
                type="checkbox"
                className="case-cacher"
                checked={annonce.cachee}
                aria-label={`Cacher l’annonce ${annonce.intitule}`}
                title={annonce.cachee ? 'Remettre cette annonce dans ma liste' : 'Ranger cette annonce hors de ma liste'}
                onChange={(e) => void cacher(annonce, e.target.checked)}
              />
              <Link to={brouillon ? `/annonces/${annonce.id}/edition` : `/annonces/${annonce.id}`}>
                <div className="titre">
                  <strong>{annonce.intitule}</strong>
                  {annonce.canal === 'reseaux_sociaux' && (
                    <span className="badge-rs" title="Publiée sur les réseaux sociaux">
                      RS
                    </span>
                  )}
                  <code>{annonce.reference ?? 'sans référence'}</code>
                </div>
                <div className="compteurs">
                  <span className={`statut-annonce statut-${annonce.statut}`}>
                    {libelleStatut(annonce.statut)}
                  </span>
                  {!brouillon && (
                    <>
                      <span>{annonce.candidatures} reçues</span>
                      <span>{annonce.aContacter} à contacter</span>
                      <span>{annonce.rendezVous} rendez-vous</span>
                    </>
                  )}
                  <em>{annonce.famille}</em>
                  {/* Annonce reconstituée depuis une candidature reçue : publiée,
                      mais sans texte ni critères (docs/INGESTION_EMAIL.md). */}
                  {annonce.origine === 'detectee' && (
                    <span className="a-completer">Détectée — à compléter</span>
                  )}
                </div>
              </Link>
              {!brouillon && (
                <Link className="gerer" to={`/annonces/${annonce.id}/edition`}>
                  Texte et publications
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
